import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 40,
    alignItems: "center",
    alignSelf: "center",
    flexWrap: "wrap",
  },
  flexRowSocialMedia: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    alignSelf: "center",
    flexWrap: "wrap",
  },
});

export default function SocialMedia() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const [website, setWebsite] = useState(communityMembership.profile.website ?? "");
  const websiteInput = useRef<any>(null);

  const [instagram, setInstagram] = useState(communityMembership.profile.instagram ?? "");
  const instagramInput = useRef<any>(null);

  const [tiktok, setTiktok] = useState(communityMembership.profile.tiktok ?? "");
  const tiktokInput = useRef<any>(null);

  const [snapchat, setSnapchat] = useState(communityMembership.profile.snapchat ?? "");
  const snapchatInput = useRef<any>(null);

  const [facebook, setFacebook] = useState(communityMembership.profile.facebook ?? "");
  const facebookInput = useRef<any>(null);

  const [x, setX] = useState(communityMembership.profile.x ?? "");
  const xInput = useRef<any>(null);

  const onSubmit = useCallback(async (skip: boolean) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "SocialMedia.onSubmit",
        "/rabbl/api/settings",
        "POST",
        {
          domain: domain,
          socialmedia: 1,
          instagram: instagram,
          tiktok: tiktok,
          snapchat: snapchat,
          facebook: facebook,
          x: x,
          website: website,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextOptionalPageLink("socialmedia", membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(skip), true);
      AppUtilities.handleException(e, "SocialMedia.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [router, domain, website, instagram, tiktok, snapchat, facebook, x]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Social Media"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/icons/externallink.png')}
            />
            <AppText>Website</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={websiteInput}
              value={website}
              onChangeText={setWebsite}
              placeholder="Website"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => instagramInput.current?.focus()}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/brands/instagramicon.png')}
            />
            <AppText>Instagram</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={instagramInput}
              value={instagram}
              onChangeText={setInstagram}
              placeholder="Instagram"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => tiktokInput.current?.focus()}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/brands/tiktokicon.png')}
            />
            <AppText>TikTok</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={tiktokInput}
              value={tiktok}
              onChangeText={setTiktok}
              placeholder="TikTok"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => snapchatInput.current?.focus()}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/brands/snapchaticon.png')}
            />
            <AppText>Snapchat</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={snapchatInput}
              value={snapchat}
              onChangeText={setSnapchat}
              placeholder="Snapchat"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => facebookInput.current?.focus()}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/brands/facebookicon.png')}
            />
            <AppText>Facebook</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={facebookInput}
              value={facebook}
              onChangeText={setFacebook}
              placeholder="Facebook"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => xInput.current?.focus()}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRowSocialMedia}>
            <Image
              style={AppConstants.STYLES.brandIcon}
              source={require('@/assets/images/brands/xicon.png')}
            />
            <AppText>X</AppText>
          </View>

          <View style={styles.inputWrapper}>
            <TextInput
              ref={xInput}
              value={x}
              onChangeText={setX}
              placeholder="X"
              style={styles.formElement}
              returnKeyType="next"
              onSubmitEditing={() => onSubmit(false)}
              autoCapitalize='none'
              autoCorrect={false}
            />
          </View>

          <View style={styles.flexRow}>
            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(false)}
              >{ edit ? "Save" : "Next" }</Button95>
            </View>

            {
              (!edit || edit.length == 0) && (
                <>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => onSubmit(true)}
                    >Skip</Button95>
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => router.navigate(AppUtils.getProfileCreateNextPageLink(communityMembership) as RelativePathString)}
                    >Skip all</Button95>
                  </View>
                </>
              )
            }
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
